export type WebsiteConsent = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  updatedAt: string;
  version: 1;
};

export type ConsentCategory = "analytics" | "marketing";

const consentStorageKey = "tolliq.websiteConsent.v1";
const consentEventName = "tolliq:consent-updated";

export function readWebsiteConsent(): WebsiteConsent | null {
  try {
    const raw = window.localStorage.getItem(consentStorageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<WebsiteConsent>;
    if (parsed.version !== 1 || parsed.necessary !== true) return null;
    return {
      necessary: true,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : new Date(0).toISOString(),
      version: 1
    };
  } catch {
    return null;
  }
}

export function saveWebsiteConsent(consent: Pick<WebsiteConsent, ConsentCategory>): WebsiteConsent {
  const nextConsent: WebsiteConsent = {
    necessary: true,
    analytics: consent.analytics,
    marketing: consent.marketing,
    updatedAt: new Date().toISOString(),
    version: 1
  };

  window.localStorage.setItem(consentStorageKey, JSON.stringify(nextConsent));
  window.dispatchEvent(new CustomEvent<WebsiteConsent>(consentEventName, { detail: nextConsent }));
  return nextConsent;
}

export function hasWebsiteConsent(category: ConsentCategory): boolean {
  return Boolean(readWebsiteConsent()?.[category]);
}

export function subscribeToWebsiteConsent(listener: (consent: WebsiteConsent | null) => void) {
  const handleConsentEvent = (event: Event) => {
    listener(event instanceof CustomEvent ? (event.detail as WebsiteConsent) : readWebsiteConsent());
  };
  const handleStorageEvent = (event: StorageEvent) => {
    if (event.key === consentStorageKey) listener(readWebsiteConsent());
  };

  window.addEventListener(consentEventName, handleConsentEvent);
  window.addEventListener("storage", handleStorageEvent);

  return () => {
    window.removeEventListener(consentEventName, handleConsentEvent);
    window.removeEventListener("storage", handleStorageEvent);
  };
}
